import type { GraphQLResult } from 'aws-amplify/api'
import { useQuery } from '@tanstack/react-query'

import { getAmplifyClient } from '@/services/amplifyClient'
import {
  fetchSupportPayload,
  useSupportTarget,
} from '@/services/support/support.client'
import {
  supportQueryKey,
  type SupportTarget,
} from '@/services/support/supportContract'
import { organizationEntitlementsQuery } from '@/services/graphql'
import { transactionalQueryPreset } from '@/services/query-presets'

const client = getAmplifyClient()

export type OrganizationEntitlementQuota = {
  quotaType:
    | 'PRODUCTS'
    | 'PROJECTS'
    | 'SUB_TEAMS'
    | 'SEATS'
    | 'QUOTE_SIGNATURES'
  limit: number
  used: number
  remaining: number
  enforcementLimit: number
  temporaryUsed: number
  required: number
  shortfall: number
}

export type CallerSeatAccess = {
  state:
    | 'PAID'
    | 'GRACE'
    | 'SUSPENDED'
    | 'WAITING_CAPACITY'
    | 'INELIGIBLE'
    | 'EXEMPT'
  reason?: string | null
  graceStartedAt?: string | null
  graceEndsAt?: string | null
}

export type OrganizationEntitlements = {
  state: 'AVAILABLE' | 'UNAVAILABLE' | 'AMBIGUOUS'
  reason: string
  access: 'FULL' | 'READ_ONLY'
  organizationID?: string | null
  licenseOrganizationID?: string | null
  inherited: boolean
  subscriptionID?: string | null
  subscriptionStatus?: string | null
  graceEndsAt?: string | null
  callerSeatAccess?: CallerSeatAccess | null
  quotas: OrganizationEntitlementQuota[]
}

export const subscriptionEntitlementKeys = {
  all: ['organization-entitlements'] as const,
  organization: (organizationID: string | null | undefined) =>
    [...subscriptionEntitlementKeys.all, organizationID] as const,
}

export const fetchOrganizationEntitlements = async (
  expectedOrganizationID: string,
  supportTarget?: SupportTarget | null,
  signal?: AbortSignal,
): Promise<OrganizationEntitlements> => {
  if (supportTarget) {
    const entitlements = await fetchSupportPayload<OrganizationEntitlements>(
      `query ConsoleSupportOrganizationEntitlements($targetUserID: ID!, $organizationID: ID!) {
      consoleSupportOrganizationEntitlements(targetUserID: $targetUserID, organizationID: $organizationID) { payload }
    }`,
      'consoleSupportOrganizationEntitlements',
      {
        targetUserID: supportTarget.targetUserID,
        organizationID: expectedOrganizationID,
      },
      signal,
    )
    if (entitlements?.organizationID !== expectedOrganizationID)
      throw new Error(
        'Organization entitlements do not match the active organization',
      )
    return entitlements
  }
  const result = (await client.graphql({
    query: organizationEntitlementsQuery,
  })) as GraphQLResult<{
    organizationEntitlements?: OrganizationEntitlements | null
  }>
  const { data, errors } = result

  if (errors?.length || !data?.organizationEntitlements) {
    throw new Error('Organization entitlements are unavailable')
  }

  if (data.organizationEntitlements.organizationID !== expectedOrganizationID) {
    throw new Error(
      'Organization entitlements do not match the active organization',
    )
  }

  return data.organizationEntitlements
}

/**
 * Data derives the active organization from authenticated resolver context.
 * The local ID only scopes React Query; it is intentionally not sent to GraphQL.
 */
export const useOrganizationEntitlements = (
  activeOrganizationID: string | null | undefined,
) => {
  const supportTarget = useSupportTarget()
  return useQuery({
    queryKey: supportQueryKey(
      subscriptionEntitlementKeys.organization(activeOrganizationID),
      supportTarget,
    ),
    queryFn: ({ signal }) =>
      fetchOrganizationEntitlements(
        activeOrganizationID!,
        supportTarget,
        signal,
      ),
    enabled: Boolean(activeOrganizationID),
    ...transactionalQueryPreset,
    refetchOnMount: true,
  })
}
